import React from "react";
import { SupabaseMark, ReactMark, MetabaseMark } from "./BrandMarks";
import "./OpenSource.css";

/* Renders `backticked` spans as inline code. */
const withCode = (text) =>
  text.split(/(`[^`]+`)/g).map((part, i) =>
    part.startsWith("`") && part.endsWith("`") ? (
      <code className="oss__code" key={i}>
        {part.slice(1, -1)}
      </code>
    ) : (
      part
    )
  );

const PROJECTS = [
  {
    name: "Supabase",
    Mark: SupabaseMark,
    slug: "supabase",
    url: "https://github.com/supabase",
  },
  {
    name: "React",
    Mark: ReactMark,
    slug: "react",
    url: "https://github.com/react",
  },
  {
    name: "Metabase",
    Mark: MetabaseMark,
    slug: "metabase",
    url: "https://github.com/metabase",
  },
];

const LEDE = {
  num: "01",
  repo: "supabase/postgres-meta → supabase/sdk",
  status: "Released in postgrest-typegen 0.2.1",
  title: "JSON columns in the Python type generator",
  meta: "August 2026",
  standfirst:
    "Every generated pydantic model with a `json` or `jsonb` column failed validation. The pull request was closed unmerged during a repo migration, then ported with credit and released.",
  trace: [
    {
      k: "The report",
      t: "`supabase-py#1597`: JSON columns don't deserialize correctly. Open, no root cause identified.",
    },
    {
      k: "Locating it",
      t: "The failing models are generated, not hand-written, so the bug is in the generator rather than the client library. That put it in `postgres-meta`'s Python template.",
    },
    {
      k: "Root cause",
      t: "The template mapped `json` and `jsonb` to pydantic's `Json[Any]`. `Json[T]` expects a JSON string and parses it, but PostgREST returns these columns already parsed, so `model_validate` raised on every model with a JSON column.",
    },
    {
      k: "The change",
      t: "Map both to `JsonValue`, pydantic's type for an already-parsed value. Two lines in the type map, no custom type or wrapper class.",
    },
    {
      k: "Evidence in the PR",
      t: "Regenerated all four targets. TypeScript, Go and Swift output was byte-identical before and after, so the change was scoped to one generator. Full suite green.",
    },
    {
      k: "Outcome",
      t: "Supabase was consolidating type generation into a shared `postgrest-typegen` package, so the maintainer triaged the open template PRs and re-landed the worthwhile ones there. Mine was ported with credit in `sdk#124`.",
    },
  ],
  quote:
    "Your fix (mapping json/jsonb to pydantic's JsonValue so deserialized PostgREST payloads validate) has been ported in supabase/sdk#124 with credit to this PR.",
  quoteAttr: "spydon · Supabase maintainer",
  links: [
    { label: "Pull request", url: "https://github.com/supabase/postgres-meta/pull/1129" },
    { label: "Where it landed", url: "https://github.com/supabase/sdk/pull/124" },
    { label: "Commit", url: "https://github.com/supabase/sdk/commit/d86db01" },
    { label: "Original report", url: "https://github.com/supabase/supabase-py/issues/1597" },
  ],
};

const LEDGER = [
  {
    num: "02",
    repo: "react/react",
    title: "Hydration halts when anything inside <head> suspends",
    contribution:
      "`<head>` is a singleton scope: entering it saves the outer hydration cursor to a module-level slot. Replaying a suspended singleton re-enters without exiting first, so the second save overwrites that slot with `null` and hydration can't resume. The server-rendered body is discarded and rebuilt on the client. Two lines in the reconciler, three regression tests, and a table covering why `HostSingleton` is the only tag affected.",
    meta: "PR #37630 · +184 / −7 · 5,383 tests green · open",
    links: [
      { label: "PR #37630", url: "https://github.com/react/react/pull/37630" },
      { label: "Issue #37551", url: "https://github.com/react/react/issues/37551" },
    ],
  },
  {
    num: "03",
    repo: "metabase/metabase",
    title: "Setup modal unmounts before its ready state renders",
    contribution:
      "`MetabaseAIProviderSetup` calls `onConnect` as soon as the create-provider mutation resolves. Five components up the chain that callback unmounts the component, and with it the modal polling for provisioning, so the modal's ready state and its Done button are unreachable. Split the mutation from the callback so `onConnect` fires on completion rather than on response.",
    meta: "PR #82245 · +159 / −5 · 498 tests green · in review",
    links: [
      { label: "PR #82245", url: "https://github.com/metabase/metabase/pull/82245" },
      { label: "Issue #82219", url: "https://github.com/metabase/metabase/issues/82219" },
    ],
  },
  {
    num: "04",
    repo: "supabase/postgres-meta",
    title: "end() resolves before the connection pool closes",
    contribution:
      "`Sentry.startSpan(...)` is called without `return`, discarding the promise that wraps `await _pool.end()`. `end()` is declared `() => Promise<void>` and awaited at 65 call sites, all of which resolve early. The three sibling span calls in the same file return theirs. Filed the issue and the one-word fix, with a regression test that asserts ordering rather than elapsed time.",
    meta: "Issue #1150 + PR #1151 · labeled bug · open",
    links: [
      { label: "Issue #1150", url: "https://github.com/supabase/postgres-meta/issues/1150" },
      { label: "PR #1151", url: "https://github.com/supabase/postgres-meta/pull/1151" },
    ],
  },
  {
    num: "05",
    repo: "supabase/sdk",
    title: "Swift generator emits a reference to an undeclared type",
    contribution:
      "`pgTypeToSwiftType` appends `Select` to every composite it resolves. That's correct for relation row types and wrong for standalone composites, which are declared without the suffix, so the output references `AddressSelect` and won't compile. Narrowing the lookup regresses materialized views to `AnyJSON` instead; the distinction that holds is `attributes`. Labeled `Typegen` and parked for the monorepo generator work.",
    meta: "Issue #154 · labeled Typegen · parked",
    links: [{ label: "Issue #154", url: "https://github.com/supabase/sdk/issues/154" }],
  },
];

const Links = ({ links, className = "" }) => (
  <div className={`oss__links ${className}`}>
    {links.map((lk, i) => (
      <React.Fragment key={lk.label}>
        {i > 0 && (
          <span className="oss__links-dot" aria-hidden>
            ·
          </span>
        )}
        <a href={lk.url} target="_blank" rel="noopener noreferrer">
          {lk.label} <span aria-hidden>→</span>
        </a>
      </React.Fragment>
    ))}
  </div>
);

const OpenSource = () => (
  <section id="open-source" className="oss" aria-labelledby="oss-heading">
    <div className="oss__head">
      <h2 className="oss__h2" id="oss-heading">
        Contributing to
        <span className="visually-hidden"> Supabase, React and Metabase</span>
      </h2>
    </div>

    <ul className="oss__logos" aria-label="Open-source projects contributed to">
      {PROJECTS.map(({ name, Mark, slug, url }) => (
        <li className={`oss__logo-slot oss__logo-slot--${slug}`} key={name}>
          <a
            className="oss__logo-link"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${name} on GitHub`}
          >
            <Mark className="oss__logo" aria-hidden="true" focusable="false" />
            <span className="oss__wordmark">{name}</span>
            <span className="oss__tip" aria-hidden>
              {name}
            </span>
          </a>
        </li>
      ))}
    </ul>

    <article className="oss__lede">
      <div className="oss__lede-inner">
        <span className="oss__lede-num" aria-hidden>
          {LEDE.num}
        </span>

        <div className="oss__lede-body">
          <div className="oss__lede-top">
            <span className="oss__repo">{LEDE.repo}</span>
            <span className="oss__status">{LEDE.status}</span>
          </div>

          <h3 className="oss__lede-title">
            <a href={LEDE.links[0].url} target="_blank" rel="noopener noreferrer">
              {LEDE.title}
            </a>
          </h3>
          <span className="oss__lede-meta">{LEDE.meta}</span>
          <p className="oss__lede-stand">{withCode(LEDE.standfirst)}</p>

          <div className="oss__trace">
            <span className="oss__trace-kick">How it was traced</span>
            <ol className="oss__trace-list">
              {LEDE.trace.map((step, i) => (
                <li className="oss__trace-step" key={step.k}>
                  <span className="oss__trace-idx" aria-hidden>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="oss__trace-content">
                    <span className="oss__trace-key">{step.k}</span>
                    <p className="oss__trace-text">{withCode(step.t)}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <blockquote className="oss__pull">
            <p className="oss__pull-text">
              {"“"}
              {LEDE.quote}
              {"”"}
            </p>
            <cite className="oss__pull-attr">— {LEDE.quoteAttr}</cite>
          </blockquote>

          <Links links={LEDE.links} />
        </div>
      </div>
    </article>

    <ol className="oss__ledger">
      {LEDGER.map((e) => (
        <li className="oss__row" key={e.num}>
          <span className="oss__row-num" aria-hidden>
            {e.num}
          </span>
          <div className="oss__row-body">
            <span className="oss__repo">{e.repo}</span>
            <h3 className="oss__row-title">
              <a href={e.links[0].url} target="_blank" rel="noopener noreferrer">
                {e.title}
              </a>
            </h3>
            <p className="oss__row-desc">{withCode(e.contribution)}</p>
            <span className="oss__row-meta">{e.meta}</span>
            <Links links={e.links} className="oss__links--sm" />
          </div>
        </li>
      ))}
    </ol>

    <p className="oss__foot">
      Each entry links to its issue or pull request. Project names and logos are
      trademarks of their respective owners; this site is not affiliated with,
      sponsored by, or endorsed by them.
    </p>
  </section>
);

export default OpenSource;
